import type { QueryClient } from '@tanstack/react-query';
import type { ComplianceReportDetailDto } from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';

/**
 * The report-detail row's side of the "optimistic mutations that carry a concurrency token" recipe
 * (apps/web/CLAUDE.md). A report row takes several treasurer actions in quick succession — confirm
 * filed, then record the receipt; override the opening balance, then reopen — and each echoes
 * `concurrencyVersion`. The server returns the freshly bumped token on every one of them; writing it
 * back into the cached row on success is what keeps the NEXT action from re-sending the old token and
 * 409-ing against the user's own previous click (a self-race), since a settle refetch can be cancelled
 * by that next action's `onMutate`.
 */

export type ReportDetailSnapshot = ComplianceReportDetailDto | undefined;

/**
 * Serialize one report row's mutations; different reports stay parallel. Note a queued mutation keeps
 * the variables (token included) it was called with — the success write-back, not the queue, is what
 * gives the NEXT click a fresh token once the row re-renders.
 */
export function reportRowScope(reportId: string): { id: string } {
  return { id: `report-row:${reportId}` };
}

/**
 * `onMutate`: cancel in-flight detail refetches (they would overwrite the patch), snapshot the row for
 * rollback, and apply the fields the client already knows the outcome of. Pass no patch when the
 * server computes everything that changes (mark filed / reopen) — the snapshot still guards rollback.
 */
export async function optimisticallyPatchReportDetail(
  queryClient: QueryClient,
  reportId: string,
  patch: Partial<ComplianceReportDetailDto> = {},
): Promise<ReportDetailSnapshot> {
  const key = queryKeys.finance.reports.detail(reportId);
  await queryClient.cancelQueries({ queryKey: key });
  const snapshot = queryClient.getQueryData<ComplianceReportDetailDto>(key);
  if (snapshot) queryClient.setQueryData<ComplianceReportDetailDto>(key, { ...snapshot, ...patch });
  return snapshot;
}

/** `onError`: restore the pre-mutation row. */
export function rollbackReportDetail(
  queryClient: QueryClient,
  reportId: string,
  snapshot: ReportDetailSnapshot,
): void {
  if (snapshot) queryClient.setQueryData(queryKeys.finance.reports.detail(reportId), snapshot);
}

/** `onSuccess`: write ONLY the server's returned token back into the cached row. */
export function reconcileReportDetailVersion(
  queryClient: QueryClient,
  reportId: string,
  concurrencyVersion: number,
): void {
  queryClient.setQueryData<ComplianceReportDetailDto>(
    queryKeys.finance.reports.detail(reportId),
    (row) => (row ? { ...row, concurrencyVersion } : row),
  );
}
